// frontend/src/store/announcementStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import announcementAPI from '../services/announcement.api';

const useAnnouncementStore = create(
  persist(
    (set, get) => ({
      // State
      announcements: [],
      currentPromotion: null,
      dismissedTimestamps: {}, // { announcementId: timestamp }
      isLoading: false,
      error: null,
      lastFetchTime: null,
      lastPromotionFetch: null,

      // =================== FETCH ANNOUNCEMENTS ===================
      
      getActiveAnnouncements: async (forceRefresh = false) => {
        const { lastFetchTime, announcements } = get();
        const now = Date.now();
        
        // Cache for 5 minutes
        if (!forceRefresh && lastFetchTime && (now - lastFetchTime) < 300000) {
          return { data: announcements };
        }

        set({ isLoading: true, error: null });
        try {
          const response = await announcementAPI.getActiveAnnouncements();
          
          set({
            announcements: response.data || [],
            lastFetchTime: now,
            isLoading: false,
            error: null,
          });
          
          return response;
        } catch (error) {
          if (error.response?.status === 404) {
            set({
              announcements: [],
              isLoading: false,
              error: null,
            });
            return { data: [] };
          }
          
          const message = error.response?.data?.error?.message || error.message;
          set({ isLoading: false, error: message });
          throw error;
        }
      },

      getRecurrence: (announcementId) => {
        const { announcements } = get();
        const announcement = announcements.find(a => a.id === announcementId);
        return announcement?.recurrence || 3600000;
      },

      shouldShowAnnouncement: (announcementId) => {
        const { dismissedTimestamps, getRecurrence } = get();
        if (!dismissedTimestamps[announcementId]) return true;
        
        const dismissedAt = dismissedTimestamps[announcementId];
        const now = Date.now();
        const recurrence = getRecurrence(announcementId);
        
        if (recurrence === 0) return false;
        
        return (now - dismissedAt) > recurrence;
      },

      cleanupExpiredTimestamps: () => {
        const { announcements, dismissedTimestamps } = get();
        const now = Date.now();
        let hasChanges = false;
        const newTimestamps = { ...dismissedTimestamps };
        
        const dismissedIds = Object.keys(dismissedTimestamps);
        if (dismissedIds.length === 0) return false;
        
        announcements.forEach(announcement => {
          const timestamp = dismissedTimestamps[announcement.id];
          if (timestamp !== undefined) {
            const recurrence = announcement.recurrence || 3600000;
            if (recurrence !== 0 && (now - timestamp) > recurrence) {
              delete newTimestamps[announcement.id];
              hasChanges = true;
            }
          }
        });
        
        if (hasChanges) {
          set({ dismissedTimestamps: newTimestamps });
        }
        
        return hasChanges;
      },

      getVisibleAnnouncements: () => {
        const { announcements } = get();
        return announcements.filter(a => get().shouldShowAnnouncement(a.id));
      },

      dismissAnnouncement: (announcementId) => {
        const now = Date.now();
        set(state => ({
          dismissedTimestamps: {
            ...state.dismissedTimestamps,
            [announcementId]: now,
          },
        }));
        // Cleanup after dismissal to show next announcement
        get().cleanupExpiredTimestamps();
      },

      clearDismissedAnnouncements: () => {
        set({ dismissedTimestamps: {} });
      },

      trackAnnouncementClick: async (announcementId) => {
        try {
          await announcementAPI.trackAnnouncementClick(announcementId);
        } catch (error) {
          console.error('Failed to track announcement click:', error);
        }
      },

      // =================== PROMOTIONS ===================
      
      getCurrentPromotion: async (forceRefresh = false) => {
        const { lastPromotionFetch, currentPromotion } = get();
        const now = Date.now();
        
        // 10 second cooldown to prevent spam
        if (!forceRefresh && lastPromotionFetch && (now - lastPromotionFetch) < 10000) {
          return { data: currentPromotion };
        }

        set({ isLoading: true, error: null });
        try {
          const response = await announcementAPI.getCurrentPromotion();
          
          set({
            currentPromotion: response.data,
            isLoading: false,
            error: null,
            lastPromotionFetch: now,
          });
          
          return response;
        } catch (error) {
          if (error.response?.status === 404) {
            set({
              currentPromotion: null,
              isLoading: false,
              error: null,
              lastPromotionFetch: now,
            });
            return { data: null };
          }
          
          const message = error.response?.data?.error?.message || error.message;
          set({ isLoading: false, error: message, lastPromotionFetch: now });
          throw error;
        }
      },

      trackPromotionClick: async (promotionId) => {
        try {
          await announcementAPI.trackPromotionClick(promotionId);
        } catch (error) {
          console.error('Failed to track promotion click:', error);
        }
      },

      reset: () => {
        set({
          announcements: [],
          currentPromotion: null,
          dismissedTimestamps: {},
          isLoading: false,
          error: null,
          lastFetchTime: null,
          lastPromotionFetch: null,
        });
      },
    }),
    {
      name: 'announcement-storage',
      partialize: (state) => ({
        dismissedTimestamps: state.dismissedTimestamps,
      }),
    }
  )
);

export default useAnnouncementStore;